import React, { useState, useEffect } from 'react';
import { Play, Clock, Radio, Mic, Search, ChevronRight } from 'lucide-react';
import { usePlayer } from '@/contexts/PlayerContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDuration(s) {
  if (!s || s <= 0) return '';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function CatchUpPage() {
  const [episodes, setEpisodes] = useState([]);
  const [shows, setShows] = useState([]);
  const [selectedShow, setSelectedShow] = useState('');
  const { playTrack } = usePlayer();

  useEffect(() => {
    axios.get(`${API}/shows`).then(r => setShows(r.data.shows || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedShow) params.set('show_id', selectedShow);
    params.set('limit', '50');
    axios.get(`${API}/episodes?${params.toString()}`).then(r => setEpisodes(r.data.episodes || [])).catch(() => {});
  }, [selectedShow]);

  const playEpisode = (ep) => {
    playTrack({
      id: `episode-${ep.id}`,
      title: ep.title,
      artist: ep.show?.name || 'Christovibes Radio',
      genre: 'Podcast'
    }, []);
    // Use the episode stream URL
    const audio = document.querySelector('audio') || new Audio();
    audio.src = `${API}/stream/episode/${ep.id}`;
    audio.play().catch(() => {});
  };

  return (
    <div data-testid="catchup-page" className="pb-28 min-h-screen">
      {/* Header */}
      <div className="relative py-20 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#991B1B]/8 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">On Demand</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-semibold text-white mb-4" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            Catch Up
          </h1>
          <p className="text-[#94A3B8] text-base sm:text-lg max-w-xl">
            Missed a show? Listen to recorded episodes and past broadcasts at your own pace.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Show Filter */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          <button
            data-testid="filter-all-shows"
            onClick={() => setSelectedShow('')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
              !selectedShow ? 'bg-[#D4AF37] text-[#0F1115]' : 'bg-[#171A21] text-[#94A3B8] hover:bg-white/10 border border-white/5'
            }`}
          >
            All Shows
          </button>
          {shows.map(show => (
            <button
              key={show.id}
              data-testid={`filter-show-${show.id}`}
              onClick={() => setSelectedShow(show.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                selectedShow === show.id ? 'bg-[#D4AF37] text-[#0F1115]' : 'bg-[#171A21] text-[#94A3B8] hover:bg-white/10 border border-white/5'
              }`}
            >
              {show.name}
            </button>
          ))}
        </div>

        {/* Episodes List */}
        {episodes.length === 0 ? (
          <div className="text-center py-20 bg-[#171A21] rounded-2xl border border-white/5">
            <Mic size={48} className="mx-auto text-[#94A3B8]/30 mb-4" />
            <p className="text-[#94A3B8] text-lg">No episodes available yet</p>
            <p className="text-[#94A3B8]/60 text-sm mt-2">Recorded shows will appear here once uploaded by the station</p>
          </div>
        ) : (
          <div className="space-y-3">
            {episodes.map((ep, i) => (
              <div
                key={ep.id}
                data-testid={`episode-card-${i}`}
                className="bg-[#171A21] rounded-2xl border border-white/5 p-5 sm:p-6 flex gap-4 sm:gap-5 hover:-translate-y-0.5 transition-transform group animate-fade-in-up"
                style={{ animationDelay: `${i * 60}ms`, opacity: 0 }}
              >
                {/* Play Button */}
                <button
                  data-testid={`play-episode-${i}`}
                  onClick={() => playEpisode(ep)}
                  className="w-14 h-14 shrink-0 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors group-hover:bg-[#D4AF37] group-hover:text-[#0F1115]"
                >
                  <Play size={22} className="text-[#D4AF37] ml-0.5 group-hover:text-[#0F1115]" />
                </button>

                {/* Episode Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {ep.show && (
                      <span className="text-xs text-[#D4AF37] font-semibold uppercase tracking-wider">{ep.show.name}</span>
                    )}
                    <span className="text-xs text-[#94A3B8]/50">S{ep.season} E{ep.episode_number}</span>
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-1 truncate">{ep.title}</h3>
                  {ep.description && <p className="text-[#94A3B8] text-sm line-clamp-2">{ep.description}</p>}
                  <div className="flex items-center gap-4 mt-3 text-xs text-[#94A3B8]">
                    {ep.duration > 0 && (
                      <span className="flex items-center gap-1"><Clock size={12} /> {formatDuration(ep.duration)}</span>
                    )}
                    <span>{formatDate(ep.created_at)}</span>
                    {ep.play_count > 0 && <span>{ep.play_count} plays</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
